import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getCampInvoice, getAllCampDetail } from '../inventoryApi';
import {
  COLORS, SEMANTIC, Card, SectionHeader, FilterField,
  SelectInput, PrimaryButton, SecondaryButton, formatCurrency, MONTH_OPTIONS,
} from './InventoryUIKit';
import InvoiceTable from './InvoiceTable';
import InvoiceSummaryCards from './InvoiceSummaryCards';
import BatchBreakdownModal from './BatchBreakdownModal';

const normalizeItems = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const normalizeList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

/**
 * CampInvoice
 *
 * Subtab of "Inventory Invoices": GET /inventory/invoices/camp/?camp_id&month&year
 * Shows the FIFO cost of items used by one camp in a given month, with a
 * page-level "View Detailed Cost Breakdown" drill-down across all items.
 */
const CampInvoice = ({ pushToast }) => {
  const now = new Date();
  const [camps, setCamps] = useState([]);
  const [campId, setCampId] = useState('');
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showBreakdown, setShowBreakdown] = useState(false);

  // Camps aren't exposed via a dedicated list endpoint here, so derive the
  // dropdown from inventory bags (each bag carries its assigned camp).
  useEffect(() => {
    const fetchCampList = async () => {
        try {
            const data = await getAllCampDetail();

            const campList = data
                .filter(camp => camp.camp_id !== null)
                .map(camp => ({
                    id: camp.camp_id,
                    name: camp.camp_location
                }));

            setCamps(campList);

            if (campList.length > 0) {
                setCampId(campList[0].id);
            }

        } catch (error) {
            console.error(error);
            setCamps([]);
        }
    };

    fetchCampList();
}, []);



const fetchInvoice = useCallback(async () => {
  if (!campId) return;

  setLoading(true);
  setError(null);

  try {
    const result = await getCampInvoice({
      camp_id: campId,
      month,
      year,
    });

    setData(result);
  } catch (err) {
    setError(
      err.response?.data?.message ||
      err.message ||
      'Failed to load camp invoice.'
    );
  } finally {
    setLoading(false);
  }
}, [campId, month, year]);

  useEffect(() => {
  if (campId) {
    fetchInvoice();
  }
}, [campId, month, year, fetchInvoice]);

  const items = normalizeItems(data);
  const totalCost = Number(data?.total_cost || 0);
  const itemsUsed = Number(data?.total_quantity_used || 0);
  const patientsServed = data?.patients_served;
  const campName = data?.camp_name || camps.find((c) => c.id === Number(campId))?.name || '—';

  const breakdownRows = useMemo(() => {
    const rows = [];
    items.forEach((item) => {
      const batches = item.batches || item.batch_breakdown || item.fifo_breakdown || [];
      batches.forEach((b, idx) => {
        rows.push({
          key: `${item.id || item.inventory_item}-${b.id || idx}`,
          item_name: item.inventory_item_name || item.inventory_item?.item_name || item.item_name || '—',
          batch: b.invoice_number || b.batch || `Batch #${b.id ?? idx + 1}`,
          purchase_date: b.purchase_date,
          supplier: b.supplier || '—',
          quantity: b.quantity ?? b.quantity_taken ?? 0,
          unit_price: b.unit_price ?? 0,
          cost: b.cost ?? (Number(b.quantity ?? b.quantity_taken ?? 0) * Number(b.unit_price ?? 0)),
        });
      });
    });
    return rows;
  }, [items]);

  const breakdownColumns = [
    { key: 'item_name', label: 'Inventory Item' },
    { key: 'batch', label: 'Batch' },
    { key: 'purchase_date', label: 'Purchase Date', render: (r) => r.purchase_date ? new Date(r.purchase_date).toLocaleDateString('en-IN') : '—' },
    { key: 'supplier', label: 'Supplier' },
    { key: 'quantity', label: 'Quantity', align: 'right' },
    { key: 'unit_price', label: 'Unit Price', align: 'right', render: (r) => formatCurrency(r.unit_price) },
    { key: 'cost', label: 'Cost', align: 'right', render: (r) => formatCurrency(r.cost) },
  ];


  const columns = [
  {
    key: 'inventory_item_name',
    label: 'Inventory Item',
    render: (r) => r.inventory_item_name,
  },
  {
    key: 'quantity_used',
    label: 'Quantity Used',
    align: 'right',
    render: (r) => r.quantity_used,
  },
  {
    key: 'total_cost',
    label: 'Cost',
    align: 'right',
    render: (r) => formatCurrency(Number(r.total_cost)),
  },
];



  const footer = (
    <tr style={{ borderTop: `2px solid ${COLORS.mediumGrey}` }}>
      <td colSpan={2} style={{ padding: '10px 12px', fontWeight: '700', textAlign: 'right', color: COLORS.darkText }}>Grand Total</td>
      <td style={{ padding: '10px 12px', fontWeight: '700', textAlign: 'right', color: COLORS.darkText }}>{formatCurrency(totalCost)}</td>
    </tr>
  );

  return (
    <div>
      <Card style={{ padding: '16px 20px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <FilterField label="Camp" minWidth="180px">
          <SelectInput
              value={campId}
              onChange={(e) => setCampId(Number(e.target.value))}
          >
              <option value="">Select Camp</option>
          
              {camps.map(camp => (
                  <option
                      key={camp.id}
                      value={camp.id}
                  >
                      {camp.name}
                  </option>
              ))}
          </SelectInput>
          </FilterField>
          <FilterField label="Month" minWidth="140px">
            <SelectInput value={month} onChange={(e) => setMonth(Number(e.target.value))}>
              {MONTH_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </SelectInput>
          </FilterField>
          <FilterField label="Year" minWidth="110px">
            <SelectInput value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {[year, year - 1, year - 2, year - 3].map((y) => <option key={y} value={y}>{y}</option>)}
            </SelectInput>
          </FilterField>
          <div style={{ marginLeft: 'auto' }}>
            <PrimaryButton onClick={fetchInvoice} disabled={loading || !campId}>
              {loading ? 'Generating…' : '📄 Generate Invoice'}
            </PrimaryButton>
          </div>
        </div>
      </Card>

      <InvoiceSummaryCards
        cards={[
          { title: 'Camp Name', value: campName, icon: '⛺', accentColor: '#6366f1', loading, error },
          { title: 'Total Inventory Cost', value: formatCurrency(totalCost), icon: '💰', accentColor: COLORS.aquaBlue, loading, error },
          { title: 'Items Used', value: itemsUsed, icon: '📦', accentColor: COLORS.grassGreen, loading, error },
          ...(patientsServed != null ? [{ title: 'Patients Served', value: patientsServed, icon: '🩺', accentColor: COLORS.vividPurple, loading, error }] : []),
        ]}
      />

      <Card>
        <SectionHeader title="Camp Invoice" subtitle={campName !== '—' ? campName : undefined} accentColor={COLORS.aquaBlue} />
        <div style={{ padding: '16px' }}>
          <InvoiceTable
            columns={columns}
            rows={items}
            loading={loading}
            error={error}
            onRetry={fetchInvoice}
            emptyMessage="No inventory usage recorded for this camp in the selected month."
            footer={items.length > 0 ? footer : null}
            rowKey="id"
          />
        </div>
      </Card>

      {showBreakdown && (
        <BatchBreakdownModal
          title="Detailed Cost Breakdown"
          subtitle={`${campName} · ${MONTH_OPTIONS.find((m) => m.value === month)?.label} ${year}`}
          columns={breakdownColumns}
          rows={breakdownRows}
          grandTotal={breakdownRows.reduce((acc, r) => acc + Number(r.cost || 0), 0)}
          onClose={() => setShowBreakdown(false)}
        />
      )}
    </div>
  );
};

export default CampInvoice;
